// Shared query model for the words sidebar: the filter shape and sort options.
// Used by the API client (to build the Tahiti query string) and the sidebar UI.
// Filtering/sorting against live data is Tahiti's job; the MSW handler has its
// own in-store implementation (see mocks/handlers/tahiti.ts).
import type { NoyauKey } from '@/schemas/word';

// ── Sort ────────────────────────────────────────────────────────────────────

/**
 * The eight sort orders offered in the sidebar. `relevance` is the default: it
 * ranks a search's complete/closest matches first (see `search-relevance.ts`
 * and `words-nav.tsx`'s relevance-mode fetch path) and only applies while a
 * query is active — picking any other sort turns relevance mode off and is
 * sticky across subsequent searches.
 */
export const WORD_SORT_OPTIONS = [
  'relevance',
  'title-asc',
  'title-desc',
  'revised-desc',
  'revised-asc',
  'created-desc',
  'created-asc',
  'noyau',
  'to-check',
] as const;
export type WordSort = (typeof WORD_SORT_OPTIONS)[number];

export const DEFAULT_WORD_SORT: WordSort = 'relevance';

/** Human labels for the sort dropdown, in display order. */
export const WORD_SORT_LABELS: Record<WordSort, string> = {
  relevance: 'Pertinence',
  'title-asc': 'A → Z',
  'title-desc': 'Z → A',
  'revised-desc': 'Révisé · récent',
  'revised-asc': 'Révisé · ancien',
  'created-desc': 'Créé · récent',
  'created-asc': 'Créé · ancien',
  noyau: 'Noyau dominant',
  'to-check': 'À vérifier',
};

// ── Filters ───────────────────────────────────────────────────────────────────

/** A noyau filter value: one of the six colour keys, or `none` for words with
 * no dominant noyau (concepts with no sources). */
export type NoyauFilterValue = NoyauKey | 'none';

/** Revised-date presets. Each maps to an inclusive lower bound via {@link presetToSince}. */
export const DATE_PRESETS = ['7d', '30d', '90d', 'year'] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  '7d': '7 jours',
  '30d': '30 jours',
  '90d': '90 jours',
  year: 'Cette année',
};

/**
 * Active sidebar filters. Multi-select dimensions (noyaux, tags) combine as OR
 * within the dimension; the date dimension is single-select. The three
 * dimensions combine as AND, together with the text query.
 */
export type WordFilters = {
  /** Dominant-noyau colour keys, plus `none`. Empty = no noyau filter. */
  noyaux: NoyauFilterValue[];
  /** Tag labels. Empty = no tag filter. */
  tags: string[];
  /** Revised-since preset, or null when no date filter is active. */
  revisedPreset: DatePreset | null;
  /** When true, show only words flagged `toCheck` (Tahiti `to_check`). */
  toCheckOnly: boolean;
};

export const EMPTY_FILTERS: WordFilters = {
  noyaux: [],
  tags: [],
  revisedPreset: null,
  toCheckOnly: false,
};

/** Number of active filter dimensions (0–4) — drives the panel badge. */
export function activeFilterCount(filters: WordFilters): number {
  let count = 0;
  if (filters.noyaux.length > 0) count += 1;
  if (filters.tags.length > 0) count += 1;
  if (filters.revisedPreset !== null) count += 1;
  if (filters.toCheckOnly) count += 1;
  return count;
}

export function hasActiveFilters(filters: WordFilters): boolean {
  return activeFilterCount(filters) > 0;
}

/**
 * Inclusive lower-bound ISO date (YYYY-MM-DD) for a preset, relative to `now`.
 * `7d`/`30d`/`90d` subtract that many days; `year` is January 1st of `now`'s year.
 * `now` is injected so the computation stays pure and testable.
 */
export function presetToSince(preset: DatePreset, now: Date): string {
  if (preset === 'year') {
    return `${now.getFullYear()}-01-01`;
  }
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - days);
  // Format from local calendar parts (not toISOString, which would shift the day
  // by the UTC offset) so the cutoff is the user's local calendar date.
  const month = String(cutoff.getMonth() + 1).padStart(2, '0');
  const day = String(cutoff.getDate()).padStart(2, '0');
  return `${cutoff.getFullYear()}-${month}-${day}`;
}
